import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { destroy } from "redux-form";

import { type IPlaybookPage } from "../../../app-utils/playbook.js";
import { type InputTableQuerySpec } from "../../../graphql.js";
import {
  clearSampleEditingData,
  loadSampleDataFromActiveColumns,
  saveSamples,
} from "../../../redux/modules/sample-editing.js";
import { type IAppState } from "../../../redux/reducer.js";
import { ModulesIcons } from "../../../settings/assets-settings.js";
import PlaybookWizard from "../PlaybookWizard.js";
import { type IWizardStep } from "../PlaybookWizardStep.js";
import SampleEditingGridScreen from "./SampleEditingGridScreen.js";
import SampleEditingStatusScreen, { SampleEditingStatusScreenImpl } from "./SampleEditingStatusScreen.js";

interface ISampleEditingWizardOwnProps {
  onClose: () => void;
  // The tableSpec allows to distinguish editing from a sample table and from the parent detail page sample listing
  // (the sample data for these two scenarios is located at different slices of the tableData redux store).
  // The tableSpec is passed to all downstream routines required for loading and saving the editing grid as well as to the auditing code etc.
  tableSpecWithSelection: InputTableQuerySpec;
}

interface ISampleEditingWizardAppStateProps {
  documentationUrl: string;
}

interface ISampleEditingWizardDispatchProps {
  clearSampleEditData: () => void;
  updateSamples: (tableSpec: InputTableQuerySpec) => Promise<void>;
  loadSampleEditingColumns: (tableSpec: InputTableQuerySpec) => Promise<boolean>;
}

export interface ISampleEditingWizardProps
  extends ISampleEditingWizardAppStateProps,
    ISampleEditingWizardDispatchProps,
    ISampleEditingWizardOwnProps {}

function mapStateToProps(state: IAppState, _ownProps: ISampleEditingWizardProps): ISampleEditingWizardAppStateProps {
  return {
    documentationUrl: state.config.configuration?.application?.documentationUrl,
  };
}

function mapDispatchToProps(dispatch): ISampleEditingWizardDispatchProps {
  return {
    clearSampleEditData: () => {
      dispatch(clearSampleEditingData());
      dispatch(destroy(SampleEditingStatusScreenImpl.SAMPLE_STATUS_FORM));
    },
    updateSamples: (tableSpec: InputTableQuerySpec) => dispatch(saveSamples(tableSpec)),
    loadSampleEditingColumns: (tableSpec: InputTableQuerySpec) => dispatch(loadSampleDataFromActiveColumns(tableSpec)),
  };
}

const ENTER_DATA_SCREEN = "ENTER_DATA_SCREEN";
const UPDATE_STATUS_SCREEN = "UPDATE_STATUS_SCREEN";

const sampleEditingScreens: IPlaybookPage[] = [
  {
    pageIdentifier: ENTER_DATA_SCREEN,
    title: "Enter Data",
  },
  {
    pageIdentifier: UPDATE_STATUS_SCREEN,
    title: "Update Status",
  },
];

/**
 * Playbook to modify sample attribute values.
 *
 * created on 1/31/2018
 */
class SampleEditingWizardImpl extends Component<ISampleEditingWizardProps> {
  constructor(props: ISampleEditingWizardProps) {
    super(props);
  }

  componentDidMount() {
    this.props.loadSampleEditingColumns(this.props.tableSpecWithSelection);
  }

  onClose = () => {
    this.props.onClose();
    // clear sample-edit data
    this.props.clearSampleEditData();
  };

  render(): ReactElement {
    return (
      <PlaybookWizard
        title={"Sample Data Editor"}
        steps={sampleEditingScreens}
        mapStepToScreen={this.mapStepToScreen}
        needsWideSpace={this.needsWideSpace}
        onClose={this.onClose}
        icon={ModulesIcons.SAMPLE_REGISTRATION}
        helpLink={`${this.props.documentationUrl}Sample%2bEditing%2band%2bData%2bEntry`}
      />
    );
  }

  mapStepToScreen = (page: IPlaybookPage): IWizardStep => {
    switch (page.pageIdentifier) {
      case ENTER_DATA_SCREEN:
        return <SampleEditingGridScreen playbookPage={page} />;
      case UPDATE_STATUS_SCREEN:
        return (
          <SampleEditingStatusScreen
            playbookPage={page}
            onNext={() => this.props.updateSamples(this.props.tableSpecWithSelection)}
          />
        );
    }
  };

  needsWideSpace = (page: IPlaybookPage): boolean => {
    switch (page.pageIdentifier) {
      case ENTER_DATA_SCREEN:
        return true;
      default:
        return false;
    }
  };
}

const SampleEditingWizard = connect<
  ISampleEditingWizardAppStateProps,
  ISampleEditingWizardDispatchProps,
  ISampleEditingWizardOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(SampleEditingWizardImpl);

export { SampleEditingWizard };
export default SampleEditingWizard;
